import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus,
  Edit2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Users,
  Briefcase,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataGrid,
  Dialog,
  EmptyState,
  Heading,
  Input,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Select,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/pe-fund-analytics';
import { createPeFundClient } from '@aaimplatform/institutional-types/api-clients';
import { csrfMiddleware } from '../../api/client';
import { mapPeFetchError } from './pe-error';
import { ListHeader } from '../collateral/CollateralListParts';
import {
  CAPITAL_ACTIVITY_LABELS,
  FUND_STATUS,
  FUND_STRATEGY_LABELS,
  LP_INTEREST_TYPE_LABELS,
  SECTOR_FOCUS_LABELS,
  WATERFALL_LABELS,
  labelFor,
  optionsFor,
} from './pe-labels';
import {
  formatCurrencyTotalsCompact,
  formatDate,
  formatMoney,
  formatPercent,
  totalsByCurrency,
  NOT_RECORDED,
} from '../lien/format';

// ---------------------------------------------------------------------------
// API Configuration
// ---------------------------------------------------------------------------

// CSRF double-submit echo (N8) on this page's five mutating calls: PATCH
// /pe/funds/{fundId}, POST /pe/funds, POST /pe/funds/{fundId}/lp-interests,
// POST /pe/funds/{fundId}/capital-activity and PATCH
// /pe/funds/{fundId}/lp-interests/{interestId}. pe-valuation mounts
// `enforceCsrf: true` (services/pe-valuation/src/middleware/auth.ts:98, GH
// #2653), so a cookie-authenticated mutation without the X-CSRF-Token header
// 403s. createPeFundClient returns a NEW instance per call (api-clients.ts:139),
// so the sibling CollateralMonitoring.tsx registration does not reach this one
// -- the wiring is per module, and this page had none (GH #2850). The `.use` sits
// on the line after the factory call deliberately: a sweep for an unwired PE
// client reads adjacency, and a comment block in between hides the pairing.
const peFundClient = createPeFundClient();
peFundClient.use(csrfMiddleware);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PeFund = components['schemas']['PeFund'];
type LpInterest = components['schemas']['LpInterest'];
type CapitalActivity = components['schemas']['CapitalActivity'];
type UpdateFundRequest = components['schemas']['UpdateFundRequest'];
type CreateLpInterestRequest = components['schemas']['CreateLpInterestRequest'];
type UpdateLpInterestRequest = components['schemas']['UpdateLpInterestRequest'];
type PledgeDetails = components['schemas']['PledgeDetails'];

/**
 * Fund form state. Numeric fields are held as the strings the inputs hold, so
 * an untouched optional field is '' (not entered) rather than 0 (a measured
 * zero). Rates are entered as percents and stored by the contract as decimals.
 */
// Internal: not an API contract
export interface FundFormData {
  name: string;
  managerId: string;
  managerName: string;
  strategy: components['schemas']['FundStrategy'];
  sectorFocus: components['schemas']['SectorFocus'] | '';
  vintageYear: string;
  fundSize: string;
  currency: string;
  fundStatus: components['schemas']['FundStatus'];
  fundTermYears: string;
  extensionYears: string;
  investmentPeriodYears: string;
  managementFeePercent: string;
  carriedInterestPercent: string;
  preferredReturnPercent: string;
  waterfallType: NonNullable<PeFund['waterfallType']> | '';
  gpCommitment: string;
}

// Internal: not an API contract
export interface LpFormData {
  lpName: string;
  interestType: LpInterest['interestType'];
  commitmentAmount: string;
  interestPercent: string;
  calledAmount: string;
  distributedAmount: string;
  currentNav: string;
}

// Internal: not an API contract
interface CapitalActivityFormData {
  activityType: components['schemas']['CapitalActivityType'];
  amount: string;
  activityDate: string;
  description: string;
}

/**
 * Pledge form state. The assigned LTV is entered as a percent, 0-100, and sent
 * as the fraction `PledgeDetails.assignedLtv` declares. `lendingInstitutionId`
 * has no field: the service stamps it from the session, and the contract
 * refuses a request that carries it.
 */
// Internal: not an API contract
export interface PledgeFormData {
  loanId: string;
  pledgeDate: string;
  assignedLtvPercent: string;
  gpConsentObtained: boolean;
  gpConsentDate: string;
  uccFilingNumber: string;
}

/** Which LP interest the pledge dialog acts on, and whether it records or clears. */
// Internal: not an API contract
interface PledgeDialogState {
  lp: LpInterest;
  mode: 'record' | 'clear';
}

const EMPTY_FUND_FORM: FundFormData = {
  name: '',
  managerId: '',
  managerName: '',
  strategy: 'large_cap_buyout',
  sectorFocus: '',
  vintageYear: String(new Date().getFullYear()),
  fundSize: '',
  currency: 'USD',
  fundStatus: 'fundraising',
  fundTermYears: '10',
  extensionYears: '',
  investmentPeriodYears: '',
  managementFeePercent: '',
  carriedInterestPercent: '',
  preferredReturnPercent: '',
  waterfallType: '',
  gpCommitment: '',
};

const EMPTY_LP_FORM: LpFormData = {
  lpName: '',
  interestType: 'limited_partner',
  commitmentAmount: '',
  interestPercent: '',
  calledAmount: '',
  distributedAmount: '',
  currentNav: '',
};

const EMPTY_PLEDGE_FORM: PledgeFormData = {
  loanId: '',
  pledgeDate: '',
  assignedLtvPercent: '',
  gpConsentObtained: false,
  gpConsentDate: '',
  uccFilingNumber: '',
};

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP'];
const INFLOW_ACTIVITIES = new Set(['distribution', 'recallable_distribution', 'return_of_capital']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** `format: date`, the `YYYY-MM-DD` a date input yields. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Form parsing: '' is "not entered"; anything else must parse
// ---------------------------------------------------------------------------

function parseNumber(value: string): number | undefined | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 'invalid';
}

function percentText(ratio: number | undefined): string {
  return ratio == null ? '' : String(Number((ratio * 100).toFixed(4)));
}

/**
 * A percent entered as 0-100 to the fraction the PE contracts carry (5 to
 * 0.05), at the six decimals `pe_lp_interests.interest_percent` and the fund
 * rate columns hold. One conversion for every percent input on this page.
 */
function percentToRatio(percent: number): number {
  return Number((percent / 100).toFixed(6));
}

function numberText(value: number | undefined): string {
  return value == null ? '' : String(value);
}

export function fundToForm(fund: PeFund): FundFormData {
  return {
    name: fund.name,
    managerId: fund.managerId,
    managerName: fund.managerName,
    strategy: fund.strategy,
    sectorFocus: fund.sectorFocus ?? '',
    vintageYear: String(fund.vintageYear),
    fundSize: String(fund.fundSize),
    currency: fund.currency,
    fundStatus: fund.fundStatus,
    fundTermYears: String(fund.fundTermYears),
    extensionYears: numberText(fund.extensionYears),
    investmentPeriodYears: numberText(fund.investmentPeriodYears),
    managementFeePercent: percentText(fund.managementFeeRate),
    carriedInterestPercent: percentText(fund.carriedInterestRate),
    preferredReturnPercent: percentText(fund.preferredReturn),
    waterfallType: fund.waterfallType ?? '',
    gpCommitment: numberText(fund.gpCommitment),
  };
}

/**
 * Parse the form's numeric fields. Returns the parsed values and a map of
 * field errors keyed on the form field name.
 */
function parseFundNumbers(form: FundFormData) {
  const errors: Partial<Record<keyof FundFormData, string>> = {};
  const read = (field: keyof FundFormData, opts: { required?: boolean; integer?: boolean; min?: number; max?: number }) => {
    const parsed = parseNumber(form[field] as string);
    if (parsed === undefined) {
      if (opts.required) errors[field] = 'Required.';
      return undefined;
    }
    if (parsed === 'invalid' || (opts.integer && !Number.isInteger(parsed))) {
      errors[field] = opts.integer ? 'Enter a whole number.' : 'Enter a number.';
      return undefined;
    }
    if (opts.min != null && parsed < opts.min) {
      errors[field] = `Must be ${opts.min} or more.`;
      return undefined;
    }
    if (opts.max != null && parsed > opts.max) {
      errors[field] = `Must be ${opts.max} or less.`;
      return undefined;
    }
    return parsed;
  };
  const values = {
    vintageYear: read('vintageYear', { required: true, integer: true, min: 1990 }),
    fundSize: read('fundSize', { required: true, min: 0 }),
    fundTermYears: read('fundTermYears', { required: true, integer: true, min: 1, max: 30 }),
    extensionYears: read('extensionYears', { integer: true, min: 0 }),
    investmentPeriodYears: read('investmentPeriodYears', { integer: true, min: 0 }),
    managementFeeRate: read('managementFeePercent', { min: 0, max: 100 }),
    carriedInterestRate: read('carriedInterestPercent', { min: 0, max: 100 }),
    preferredReturn: read('preferredReturnPercent', { min: 0, max: 100 }),
    gpCommitment: read('gpCommitment', { min: 0 }),
  };
  const toRatio = (percent: number | undefined) =>
    percent == null ? undefined : percentToRatio(percent);
  return {
    errors,
    values: {
      ...values,
      managementFeeRate: toRatio(values.managementFeeRate),
      carriedInterestRate: toRatio(values.carriedInterestRate),
      preferredReturn: toRatio(values.preferredReturn),
    },
  };
}

/**
 * The PATCH body for an edit: only the fields the user changed. UpdateFundRequest
 * carries every editable field except managerId, which the edit form shows
 * read-only. A recorded optional value cannot be cleared through this contract
 * (the fields are not nullable), so emptying one is a field error rather than a
 * silent no-op.
 */
export function buildFundUpdate(
  fund: PeFund,
  form: FundFormData,
): { body: UpdateFundRequest; errors: Partial<Record<keyof FundFormData, string>> } {
  const { errors, values } = parseFundNumbers(form);
  const body: UpdateFundRequest = {};

  if (!form.name.trim()) errors.name = 'Required.';
  else if (form.name.trim() !== fund.name) body.name = form.name.trim();
  if (!form.managerName.trim()) errors.managerName = 'Required.';
  else if (form.managerName.trim() !== fund.managerName) body.managerName = form.managerName.trim();
  if (form.strategy !== fund.strategy) body.strategy = form.strategy;
  if (form.fundStatus !== fund.fundStatus) body.fundStatus = form.fundStatus;
  if (form.currency !== fund.currency) body.currency = form.currency;

  if (form.sectorFocus === '') {
    if (fund.sectorFocus) errors.sectorFocus = 'A recorded sector focus cannot be cleared.';
  } else if (form.sectorFocus !== fund.sectorFocus) {
    body.sectorFocus = form.sectorFocus;
  }
  if (form.waterfallType === '') {
    if (fund.waterfallType) errors.waterfallType = 'A recorded waterfall cannot be cleared.';
  } else if (form.waterfallType !== fund.waterfallType) {
    body.waterfallType = form.waterfallType;
  }

  const numeric: Array<[keyof typeof values, keyof FundFormData, number | undefined]> = [
    ['vintageYear', 'vintageYear', fund.vintageYear],
    ['fundSize', 'fundSize', fund.fundSize],
    ['fundTermYears', 'fundTermYears', fund.fundTermYears],
    ['extensionYears', 'extensionYears', fund.extensionYears],
    ['investmentPeriodYears', 'investmentPeriodYears', fund.investmentPeriodYears],
    ['managementFeeRate', 'managementFeePercent', fund.managementFeeRate],
    ['carriedInterestRate', 'carriedInterestPercent', fund.carriedInterestRate],
    ['preferredReturn', 'preferredReturnPercent', fund.preferredReturn],
    ['gpCommitment', 'gpCommitment', fund.gpCommitment],
  ];
  for (const [key, field, current] of numeric) {
    if (errors[field]) continue;
    const next = values[key];
    if (next === undefined) {
      if (current != null) errors[field] = 'A recorded value cannot be cleared; enter a number.';
      continue;
    }
    if (next !== current) {
      (body as Record<string, number>)[key] = next;
    }
  }

  return { body, errors };
}

/** The create body for a new LP interest. */
export function buildLpCreate(
  form: LpFormData,
): { body: CreateLpInterestRequest | null; errors: Partial<Record<keyof LpFormData, string>> } {
  const errors: Partial<Record<keyof LpFormData, string>> = {};
  const read = (field: keyof LpFormData, required = false, max?: number) => {
    const parsed = parseNumber(form[field]);
    if (parsed === undefined) {
      if (required) errors[field] = 'Required.';
      return undefined;
    }
    if (parsed === 'invalid' || parsed < 0 || (max != null && parsed > max)) {
      errors[field] = max != null ? `Enter a number from 0 to ${max}.` : 'Enter a number, 0 or more.';
      return undefined;
    }
    return parsed;
  };
  if (!form.lpName.trim()) errors.lpName = 'Required.';
  const commitmentAmount = read('commitmentAmount', true);
  const calledAmount = read('calledAmount', true);
  const distributedAmount = read('distributedAmount', true);
  const currentNav = read('currentNav', true);
  // Entered as a percent, 0-100, and sent as the fraction of the fund the
  // contract declares (greater than 0, at most 1): 5 is sent as 0.05. Zero
  // names no share, and a percent too small to survive six decimals would be
  // sent as zero, so both are refused here rather than by the service.
  const ownershipPercent = read('interestPercent', false, 100);
  let interestPercent: number | undefined;
  if (ownershipPercent !== undefined) {
    interestPercent = percentToRatio(ownershipPercent);
    if (!(interestPercent > 0)) {
      errors.interestPercent = 'Enter a percentage greater than 0 (at least 0.0001) and at most 100.';
      interestPercent = undefined;
    }
  }
  if (
    Object.keys(errors).length > 0 ||
    commitmentAmount === undefined ||
    calledAmount === undefined ||
    distributedAmount === undefined ||
    currentNav === undefined
  ) {
    return { body: null, errors };
  }

  // The called, distributed and NAV amounts are required inputs. The contract
  // gives each a `default: 0` and the generated type makes them required, so an
  // amount left blank would otherwise be stored as a measured 0; the user enters
  // 0 when 0 is the figure.
  const body: CreateLpInterestRequest = {
    lpName: form.lpName.trim(),
    commitmentAmount,
    calledAmount,
    distributedAmount,
    currentNav,
    interestType: form.interestType,
  };
  if (interestPercent !== undefined) body.interestPercent = interestPercent;
  return { body, errors };
}

/**
 * The PATCH body that records a pledge on an LP interest
 * (`updateLpInterest`). `loanId`, `pledgeDate` and `assignedLtv` are required
 * to record one: the service answers 422 PLEDGE_DETAILS_REQUIRED without them,
 * so the form does not send a pledge that lacks one. The assigned LTV is
 * entered as a percent and sent as the fraction the contract declares.
 */
export function buildPledgeUpdate(
  form: PledgeFormData,
): { body: UpdateLpInterestRequest | null; errors: Partial<Record<keyof PledgeFormData, string>> } {
  const errors: Partial<Record<keyof PledgeFormData, string>> = {};

  const loanId = form.loanId.trim();
  if (!loanId) errors.loanId = 'Required.';
  else if (!UUID.test(loanId)) errors.loanId = 'Enter the loan identifier (a UUID).';

  if (!ISO_DATE.test(form.pledgeDate)) errors.pledgeDate = 'Required.';

  const ltvPercent = parseNumber(form.assignedLtvPercent);
  if (ltvPercent === undefined) {
    errors.assignedLtvPercent = 'Required.';
  } else if (ltvPercent === 'invalid' || ltvPercent < 0 || ltvPercent > 100) {
    errors.assignedLtvPercent = 'Enter a number from 0 to 100.';
  }

  if (form.gpConsentDate !== '' && !ISO_DATE.test(form.gpConsentDate)) {
    errors.gpConsentDate = 'Enter a date.';
  } else if (form.gpConsentDate !== '' && !form.gpConsentObtained) {
    errors.gpConsentDate = 'Check GP consent obtained to record its date.';
  }

  if (Object.keys(errors).length > 0 || typeof ltvPercent !== 'number') {
    return { body: null, errors };
  }

  const pledgeDetails: PledgeDetails = {
    loanId,
    pledgeDate: form.pledgeDate,
    assignedLtv: percentToRatio(ltvPercent),
    gpConsentObtained: form.gpConsentObtained,
  };
  if (form.gpConsentDate !== '') pledgeDetails.gpConsentDate = form.gpConsentDate;
  const uccFilingNumber = form.uccFilingNumber.trim();
  if (uccFilingNumber) pledgeDetails.uccFilingNumber = uccFilingNumber;

  return { body: { isPledged: true, pledgeDetails }, errors };
}

function rateOrNotRecorded(rate: number | undefined): string {
  return rate == null ? 'Not recorded' : formatPercent(rate);
}

// The three tables on this page are the kit DataGrid, as on the other list
// pages: its header treatment, its monospace figures and its row striping. They
// were hand-written <table> markup with a local header class (UI run r3,
// fi-cons-4). DataGrid draws no card of its own, so each sits flush in its
// section's surface. Pagination is off: a fund's LP interests and capital
// activity are read whole.

// ---------------------------------------------------------------------------
// Fund Administration Page
// ---------------------------------------------------------------------------

/**
 * FundAdministrationPage
 *
 * PE funds with their LP interests and capital activity, wired to the PE
 * Valuation API at /api/v1/pe/funds.
 */
export function FundAdministrationPage() {
  const [funds, setFunds] = useState<PeFund[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [selectedFundId, setSelectedFundId] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<'lp' | 'capital' | null>(null);

  const [showFundModal, setShowFundModal] = useState(false);
  const [editingFund, setEditingFund] = useState<PeFund | null>(null);
  const [fundForm, setFundForm] = useState<FundFormData>(EMPTY_FUND_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const [lpInterests, setLpInterests] = useState<LpInterest[]>([]);
  const [isLoadingLp, setIsLoadingLp] = useState(false);
  const [lpError, setLpError] = useState<string | null>(null);
  const [showLpForm, setShowLpForm] = useState(false);
  const [lpForm, setLpForm] = useState<LpFormData>(EMPTY_LP_FORM);

  const [pledgeDialog, setPledgeDialog] = useState<PledgeDialogState | null>(null);
  const [pledgeForm, setPledgeForm] = useState<PledgeFormData>(EMPTY_PLEDGE_FORM);
  const [pledgeError, setPledgeError] = useState<string | null>(null);

  const [capitalActivities, setCapitalActivities] = useState<CapitalActivity[]>([]);
  const [isLoadingCapital, setIsLoadingCapital] = useState(false);
  const [capitalError, setCapitalError] = useState<string | null>(null);
  const [showCapitalForm, setShowCapitalForm] = useState(false);
  const [capitalForm, setCapitalForm] = useState<CapitalActivityFormData>({
    activityType: 'capital_call',
    amount: '',
    activityDate: new Date().toISOString().split('T')[0],
    description: '',
  });

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------

  const fetchFunds = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const { data, error: fetchError } = await peFundClient.GET('/api/v1/pe/funds');
      if (fetchError || !data) throw new Error('Failed to load funds');
      setFunds(data.data ?? []);
    } catch (err) {
      // F-AP-PE-PARSE-002 (2026-05-17): mapPeFetchError rewrites the proxy's
      // HTML-404 JSON parse error into an operator-actionable message.
      setFunds([]);
      setLoadError(mapPeFetchError(err, 'load funds'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLpInterests = useCallback(async (fundId: string) => {
    setIsLoadingLp(true);
    setLpError(null);
    try {
      const { data, error: fetchError } = await peFundClient.GET(
        '/api/v1/pe/funds/{fundId}/lp-interests',
        { params: { path: { fundId } } }
      );
      if (fetchError || !data) throw new Error('Failed to load LP interests');
      setLpInterests(data.data ?? []);
    } catch (err) {
      setLpInterests([]);
      setLpError(mapPeFetchError(err, 'load LP interests'));
    } finally {
      setIsLoadingLp(false);
    }
  }, []);

  const fetchCapitalActivity = useCallback(async (fundId: string) => {
    setIsLoadingCapital(true);
    setCapitalError(null);
    try {
      const { data, error: fetchError } = await peFundClient.GET(
        '/api/v1/pe/funds/{fundId}/capital-activity',
        { params: { path: { fundId } } }
      );
      if (fetchError || !data) throw new Error('Failed to load capital activity');
      setCapitalActivities(data.data ?? []);
    } catch (err) {
      setCapitalActivities([]);
      setCapitalError(mapPeFetchError(err, 'load capital activity'));
    } finally {
      setIsLoadingCapital(false);
    }
  }, []);

  useEffect(() => {
    fetchFunds();
  }, [fetchFunds]);

  useEffect(() => {
    if (selectedFundId) {
      fetchLpInterests(selectedFundId);
      fetchCapitalActivity(selectedFundId);
    }
  }, [selectedFundId, fetchLpInterests, fetchCapitalActivity]);

  const selectedFund = useMemo(
    () => funds.find((f) => f.id === selectedFundId) ?? null,
    [funds, selectedFundId]
  );

  const toggleFund = useCallback(
    (fundId: string) => setSelectedFundId((current) => (current === fundId ? null : fundId)),
    [],
  );

  // -------------------------------------------------------------------------
  // Fund create / edit
  // -------------------------------------------------------------------------

  const openCreateFund = () => {
    setEditingFund(null);
    setFundForm(EMPTY_FUND_FORM);
    setShowFundModal(true);
  };

  const openEditFund = useCallback((fund: PeFund) => {
    setEditingFund(fund);
    setFundForm(fundToForm(fund));
    setShowFundModal(true);
  }, []);

  const editPlan = useMemo(
    () => (editingFund ? buildFundUpdate(editingFund, fundForm) : null),
    [editingFund, fundForm]
  );
  const createPlan = useMemo(() => {
    if (editingFund) return null;
    const { errors, values } = parseFundNumbers(fundForm);
    if (!fundForm.name.trim()) errors.name = 'Required.';
    if (!fundForm.managerName.trim()) errors.managerName = 'Required.';
    if (!UUID.test(fundForm.managerId.trim())) errors.managerId = 'Enter the manager identifier (a UUID).';
    return { errors, values };
  }, [editingFund, fundForm]);

  const formErrors = (editPlan ?? createPlan)?.errors ?? {};
  const hasFormErrors = Object.keys(formErrors).length > 0;
  const noChanges = !!editPlan && Object.keys(editPlan.body).length === 0;
  // On a new fund a required field left empty disables Create without shouting
  // 'Required.' at every empty input; an entered value that does not parse, and
  // every error while editing, shows beside its field.
  const fieldError = (field: keyof FundFormData): string | undefined =>
    editingFund || fundForm[field] !== '' ? formErrors[field] : undefined;

  const handleSaveFund = async () => {
    if (hasFormErrors) return;
    setIsSaving(true);
    setActionError(null);
    try {
      if (editingFund && editPlan) {
        const { error: patchError } = await peFundClient.PATCH('/api/v1/pe/funds/{fundId}', {
          params: { path: { fundId: editingFund.id } },
          body: editPlan.body,
        });
        if (patchError) throw new Error('Failed to update fund');
      } else if (createPlan) {
        const v = createPlan.values;
        const { error: createError } = await peFundClient.POST('/api/v1/pe/funds', {
          body: {
            name: fundForm.name.trim(),
            managerId: fundForm.managerId.trim(),
            managerName: fundForm.managerName.trim(),
            strategy: fundForm.strategy,
            sectorFocus: fundForm.sectorFocus || undefined,
            vintageYear: v.vintageYear!,
            fundSize: v.fundSize!,
            currency: fundForm.currency,
            fundTermYears: v.fundTermYears!,
            extensionYears: v.extensionYears,
            investmentPeriodYears: v.investmentPeriodYears,
            managementFeeRate: v.managementFeeRate,
            carriedInterestRate: v.carriedInterestRate,
            preferredReturn: v.preferredReturn,
            waterfallType: fundForm.waterfallType || undefined,
            gpCommitment: v.gpCommitment,
          },
        });
        if (createError) throw new Error('Failed to create fund');
      }
      setShowFundModal(false);
      await fetchFunds();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save fund');
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // LP Interest Handlers
  // -------------------------------------------------------------------------

  const lpPlan = useMemo(() => buildLpCreate(lpForm), [lpForm]);

  const handleAddLp = async () => {
    if (!selectedFundId || !lpPlan.body) return;
    setIsSaving(true);
    try {
      const { error: createError } = await peFundClient.POST(
        '/api/v1/pe/funds/{fundId}/lp-interests',
        { params: { path: { fundId: selectedFundId } }, body: lpPlan.body }
      );
      if (createError) throw new Error('Failed to add LP interest');
      setShowLpForm(false);
      setLpForm(EMPTY_LP_FORM);
      await fetchLpInterests(selectedFundId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add LP interest');
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Pledge Handlers (PATCH /api/v1/pe/funds/{fundId}/lp-interests/{interestId})
  // -------------------------------------------------------------------------

  const pledgePlan = useMemo(() => buildPledgeUpdate(pledgeForm), [pledgeForm]);

  const openPledge = useCallback((lp: LpInterest, mode: PledgeDialogState['mode']) => {
    setPledgeForm(EMPTY_PLEDGE_FORM);
    setPledgeError(null);
    setPledgeDialog({ lp, mode });
  }, []);

  const handlePledge = async () => {
    if (!selectedFundId || !pledgeDialog) return;
    const body: UpdateLpInterestRequest | null =
      pledgeDialog.mode === 'clear' ? { isPledged: false } : pledgePlan.body;
    if (!body) return;
    setIsSaving(true);
    setPledgeError(null);
    try {
      // The same client, and so the same csrfMiddleware, as every other
      // mutation on this page.
      const { error: patchError } = await peFundClient.PATCH(
        '/api/v1/pe/funds/{fundId}/lp-interests/{interestId}',
        {
          params: { path: { fundId: selectedFundId, interestId: pledgeDialog.lp.id } },
          body,
        }
      );
      if (patchError) {
        throw new Error(
          pledgeDialog.mode === 'clear' ? 'Failed to clear the pledge' : 'Failed to record the pledge',
        );
      }
      setPledgeDialog(null);
      await fetchLpInterests(selectedFundId);
    } catch (err) {
      setPledgeError(err instanceof Error ? err.message : 'Failed to update the pledge');
    } finally {
      setIsSaving(false);
    }
  };

  const setPledgeField = (patch: Partial<PledgeFormData>) =>
    setPledgeForm((prev) => ({ ...prev, ...patch }));
  // An empty required field disables Record without shouting 'Required.' at
  // it; an entered value that does not parse shows its error beside it.
  const pledgeFieldError = (field: keyof PledgeFormData): string | undefined =>
    pledgeForm[field] !== '' && pledgeForm[field] !== false ? pledgePlan.errors[field] : undefined;

  // -------------------------------------------------------------------------
  // Capital Activity Handlers
  // -------------------------------------------------------------------------

  const capitalAmount = parseNumber(capitalForm.amount);
  const capitalAmountValid = typeof capitalAmount === 'number' && capitalAmount > 0;

  const handleAddCapitalActivity = async () => {
    if (!selectedFundId || !capitalAmountValid) return;
    setIsSaving(true);
    try {
      const { error: createError } = await peFundClient.POST(
        '/api/v1/pe/funds/{fundId}/capital-activity',
        {
          params: { path: { fundId: selectedFundId } },
          body: {
            activityType: capitalForm.activityType,
            amount: capitalAmount as number,
            activityDate: capitalForm.activityDate,
            purpose: capitalForm.description.trim() || undefined,
          },
        }
      );
      if (createError) throw new Error('Failed to record capital activity');
      setShowCapitalForm(false);
      setCapitalForm({
        activityType: 'capital_call',
        amount: '',
        activityDate: new Date().toISOString().split('T')[0],
        description: '',
      });
      await fetchCapitalActivity(selectedFundId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to record capital activity');
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Summary Metrics
  // -------------------------------------------------------------------------

  const committedTotals = useMemo(
    () => totalsByCurrency(funds, (f) => f.fundSize, (f) => f.currency),
    [funds]
  );
  const activeFunds = useMemo(
    () => funds.filter((f) => f.fundStatus === 'investing' || f.fundStatus === 'evergreen').length,
    [funds]
  );
  const avgVintage = useMemo(() => {
    if (funds.length === 0) return null;
    return Math.round(funds.reduce((sum, f) => sum + f.vintageYear, 0) / funds.length);
  }, [funds]);

  const cardsRead = !isLoading && !loadError;
  const cardCaption = isLoading
    ? METRIC_READ_CAPTIONS.unread
    : loadError
      ? METRIC_READ_CAPTIONS.failed
      : undefined;

  const fundColumns = useMemo<ColumnDef<PeFund, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Fund name',
        accessorFn: (fund) => fund.name,
        cell: ({ row }) => {
          const fund = row.original;
          const selected = selectedFundId === fund.id;
          return (
            <button
              type="button"
              className={`text-left text-sm font-sans text-primary leading-normal hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm ${
                selected ? 'font-semibold underline' : 'font-medium'
              }`}
              aria-expanded={selected}
              aria-controls="fund-detail"
              onClick={() => toggleFund(fund.id)}
            >
              {fund.name}
            </button>
          );
        },
      },
      {
        id: 'managerName',
        header: 'Manager',
        accessorFn: (fund) => fund.managerName,
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary leading-normal">{row.original.managerName}</span>
        ),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (fund) => labelFor(FUND_STRATEGY_LABELS, fund.strategy),
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {labelFor(FUND_STRATEGY_LABELS, row.original.strategy)}
          </span>
        ),
      },
      {
        id: 'vintageYear',
        header: 'Vintage',
        accessorFn: (fund) => fund.vintageYear,
        meta: { align: 'center' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {row.original.vintageYear}
          </span>
        ),
      },
      {
        id: 'fundSize',
        header: 'Fund size',
        accessorFn: (fund) => fund.fundSize,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatMoney(row.original.fundSize, row.original.currency)}
          </span>
        ),
      },
      {
        id: 'fundStatus',
        header: 'Status',
        accessorFn: (fund) => fund.fundStatus,
        meta: { align: 'center' },
        cell: ({ row }) => {
          const status = FUND_STATUS[row.original.fundStatus];
          return status ? (
            <Badge variant={status.variant} size="sm">
              {status.label}
            </Badge>
          ) : (
            <span className="text-sm text-tertiary">{row.original.fundStatus}</span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <Button
            variant="tertiary"
            size="sm"
            aria-label={`Edit ${row.original.name}`}
            onClick={() => openEditFund(row.original)}
          >
            <Edit2 className="w-4 h-4" aria-hidden="true" />
          </Button>
        ),
      },
    ],
    [selectedFundId, toggleFund, openEditFund],
  );

  const fundCurrency = selectedFund?.currency ?? 'USD';

  const lpColumns = useMemo<ColumnDef<LpInterest, unknown>[]>(
    () => [
      {
        id: 'lpName',
        header: 'LP name',
        accessorFn: (lp) => lp.lpName,
        cell: ({ row }) => (
          <span className="text-sm font-sans font-medium text-primary">{row.original.lpName}</span>
        ),
      },
      {
        id: 'interestType',
        header: 'Interest type',
        accessorFn: (lp) => labelFor(LP_INTEREST_TYPE_LABELS, lp.interestType),
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {labelFor(LP_INTEREST_TYPE_LABELS, row.original.interestType)}
          </span>
        ),
      },
      {
        id: 'commitmentAmount',
        header: 'Commitment',
        accessorFn: (lp) => lp.commitmentAmount,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatMoney(row.original.commitmentAmount, fundCurrency)}
          </span>
        ),
      },
      {
        id: 'calledAmount',
        header: 'Called',
        accessorFn: (lp) => lp.calledAmount,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatMoney(row.original.calledAmount, fundCurrency)}
          </span>
        ),
      },
      {
        id: 'unfundedCommitment',
        header: 'Unfunded',
        accessorFn: (lp) => lp.unfundedCommitment ?? undefined,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary tabular-nums">
            {row.original.unfundedCommitment != null
              ? formatMoney(row.original.unfundedCommitment, fundCurrency)
              : 'Not recorded'}
          </span>
        ),
      },
      {
        id: 'currentNav',
        header: 'Current NAV',
        accessorFn: (lp) => lp.currentNav,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatMoney(row.original.currentNav, fundCurrency)}
          </span>
        ),
      },
      {
        id: 'interestPercent',
        header: 'Ownership',
        accessorFn: (lp) => lp.interestPercent ?? undefined,
        meta: { align: 'center' },
        // `interestPercent` is the fraction of the fund, 0-1, so it prints as a
        // percent directly: 0.05 is 5.00%.
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {row.original.interestPercent != null
              ? formatPercent(row.original.interestPercent, 2)
              : 'Not recorded'}
          </span>
        ),
      },
      {
        id: 'isPledged',
        header: 'Pledged',
        accessorFn: (lp) => (lp.isPledged ? 'Yes' : 'No'),
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.isPledged ? (
            <Badge variant="info" size="sm">
              Yes
            </Badge>
          ) : (
            <span className="text-xs font-sans text-tertiary">No</span>
          ),
      },
      {
        id: 'pledgeAction',
        header: 'Actions',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) =>
          row.original.isPledged ? (
            <Button
              variant="tertiary"
              size="sm"
              aria-label={`Clear pledge on ${row.original.lpName}`}
              onClick={() => openPledge(row.original, 'clear')}
            >
              Clear pledge
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Record pledge on ${row.original.lpName}`}
              onClick={() => openPledge(row.original, 'record')}
            >
              Record pledge
            </Button>
          ),
      },
    ],
    [fundCurrency, openPledge],
  );

  const capitalColumns = useMemo<ColumnDef<CapitalActivity, unknown>[]>(
    () => [
      {
        id: 'activityDate',
        header: 'Date',
        accessorFn: (activity) => activity.activityDate,
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {formatDate(row.original.activityDate)}
          </span>
        ),
      },
      {
        id: 'activityType',
        header: 'Type',
        accessorFn: (activity) => labelFor(CAPITAL_ACTIVITY_LABELS, activity.activityType),
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {labelFor(CAPITAL_ACTIVITY_LABELS, row.original.activityType)}
          </span>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorFn: (activity) => activity.amount,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const inflow = INFLOW_ACTIVITIES.has(row.original.activityType);
          return (
            <span className={`text-sm font-mono tabular-nums ${inflow ? 'text-growth' : 'text-primary'}`}>
              {inflow ? '+' : '-'}
              {formatMoney(row.original.amount, fundCurrency)}
            </span>
          );
        },
      },
      {
        id: 'purpose',
        header: 'Description',
        accessorFn: (activity) => activity.purpose ?? '',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">{row.original.purpose || NOT_RECORDED}</span>
        ),
      },
    ],
    [fundCurrency],
  );

  const sectorOptions = optionsFor(SECTOR_FOCUS_LABELS);
  const setField = (patch: Partial<FundFormData>) => setFundForm((prev) => ({ ...prev, ...patch }));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* The shared list header and tile row of the group (UI run r3, fi-g5-4). */}
      <ListHeader
        onRefresh={() => void fetchFunds()}
        refreshing={isLoading}
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Private equity fund tracking and LP interest management
          </p>
        }
      >
        <Button variant="primary" onClick={openCreateFund}>
          <Plus className="w-4 h-4" aria-hidden="true" />
          Create Fund
        </Button>
      </ListHeader>

      {loadError && (
        <Alert type="error" title="Funds could not be read" className="mb-6">
          {loadError}
        </Alert>
      )}
      {actionError && (
        <Alert type="error" className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <span>{actionError}</span>
            <Button variant="tertiary" size="sm" onClick={() => setActionError(null)} aria-label="Dismiss error">
              Dismiss
            </Button>
          </div>
        </Alert>
      )}

      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Total funds"
          value={cardsRead ? String(funds.length) : NOT_RECORDED}
          caption={cardCaption}
        />
        <MetricCard
          label="Active funds"
          value={cardsRead ? String(activeFunds) : NOT_RECORDED}
          caption={cardCaption ?? 'Investing or evergreen'}
        />
        <MetricCard
          label="Total committed capital"
          value={cardsRead ? formatCurrencyTotalsCompact(committedTotals) : NOT_RECORDED}
          caption={
            cardCaption ??
            (committedTotals.length === 0
              ? 'No funds recorded'
              : committedTotals.length > 1
                ? `Sum of fund sizes, ${committedTotals.length} currencies, not converted`
                : 'Sum of fund sizes')
          }
        />
        <MetricCard
          label="Average vintage year"
          value={cardsRead && avgVintage != null ? String(avgVintage) : NOT_RECORDED}
          caption={cardCaption}
        />
      </MetricCardGrid>

      {/* Fund List */}
      {!loadError && (
        <div className="bg-surface rounded-sm shadow-sm overflow-hidden mb-8">
          {isLoading ? (
            <div className="p-8 text-center">
              <p className="text-sm font-sans text-tertiary">Loading funds...</p>
            </div>
          ) : funds.length === 0 ? (
            <EmptyState
              icon={<Briefcase className="h-8 w-8" />}
              title="No funds are recorded for your institution"
              description="Create a fund to record its LP interests and capital activity."
            />
          ) : (
            <DataGrid<PeFund>
              data={funds}
              columns={fundColumns}
              enablePagination={false}
              minTableWidth={800}
              getRowId={(fund) => fund.id}
              hoverable
            />
          )}
        </div>
      )}

      {/* Selected Fund Detail Panels */}
      {selectedFund && (
        <div id="fund-detail" className="space-y-4">
          <div className="bg-surface rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              {/* Card scale, not the level-2 default section scale (UI run r3, fi-g5-5). */}
              <Heading level={2} size="card">{selectedFund.name}</Heading>
              {FUND_STATUS[selectedFund.fundStatus] && (
                <Badge variant={FUND_STATUS[selectedFund.fundStatus].variant} size="sm">
                  {FUND_STATUS[selectedFund.fundStatus].label}
                </Badge>
              )}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm font-sans">
              <div>
                <dt className="text-label-xs font-semibold uppercase text-tertiary mb-1">Management fee</dt>
                <dd className="text-primary font-mono tabular-nums">
                  {rateOrNotRecorded(selectedFund.managementFeeRate)}
                </dd>
              </div>
              <div>
                <dt className="text-label-xs font-semibold uppercase text-tertiary mb-1">Carried interest</dt>
                <dd className="text-primary font-mono tabular-nums">
                  {rateOrNotRecorded(selectedFund.carriedInterestRate)}
                </dd>
              </div>
              <div>
                <dt className="text-label-xs font-semibold uppercase text-tertiary mb-1">Preferred return</dt>
                <dd className="text-primary font-mono tabular-nums">
                  {rateOrNotRecorded(selectedFund.preferredReturn)}
                </dd>
              </div>
              <div>
                <dt className="text-label-xs font-semibold uppercase text-tertiary mb-1">Waterfall</dt>
                <dd className="text-primary">
                  {selectedFund.waterfallType ? WATERFALL_LABELS[selectedFund.waterfallType] : 'Not recorded'}
                </dd>
              </div>
              <div>
                <dt className="text-label-xs font-semibold uppercase text-tertiary mb-1">GP commitment</dt>
                <dd className="text-primary font-mono tabular-nums">
                  {selectedFund.gpCommitment != null
                    ? formatMoney(selectedFund.gpCommitment, selectedFund.currency)
                    : 'Not recorded'}
                </dd>
              </div>
            </dl>
          </div>

          {/* LP Interests Section */}
          <div className="bg-surface rounded-sm shadow-sm overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-elevated transition-colors"
              onClick={() => setExpandedSection(expandedSection === 'lp' ? null : 'lp')}
              aria-expanded={expandedSection === 'lp'}
              aria-controls="fund-lp-section"
            >
              <span className="flex items-center gap-3">
                <Users className="w-5 h-5 text-tertiary" aria-hidden="true" />
                <span className="text-sm font-sans font-semibold text-primary">
                  LP Interests ({isLoadingLp ? '...' : lpError ? NOT_RECORDED : lpInterests.length})
                </span>
              </span>
              {expandedSection === 'lp' ? (
                <ChevronDown className="w-4 h-4 text-tertiary" aria-hidden="true" />
              ) : (
                <ChevronRight className="w-4 h-4 text-tertiary" aria-hidden="true" />
              )}
            </button>

            {expandedSection === 'lp' && (
              <div id="fund-lp-section" className="border-t border-recessed">
                <div className="px-6 py-3 flex justify-end">
                  <Button variant="secondary" size="sm" onClick={() => setShowLpForm(!showLpForm)}>
                    <Plus className="w-3 h-3" aria-hidden="true" />
                    Add LP
                  </Button>
                </div>

                {showLpForm && (
                  <div className="px-6 pb-4 border-b border-recessed">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Input
                        id="lp-name"
                        label="LP name"
                        value={lpForm.lpName}
                        onChange={(e) => setLpForm({ ...lpForm, lpName: e.target.value })}
                      />
                      <Select
                        id="lp-interest-type"
                        label="Interest type"
                        value={lpForm.interestType}
                        onChange={(e) =>
                          setLpForm({ ...lpForm, interestType: e.target.value as LpInterest['interestType'] })
                        }
                      >
                        {optionsFor(LP_INTEREST_TYPE_LABELS).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Input
                        id="lp-commitment"
                        label="Commitment amount"
                        type="number"
                        min={0}
                        value={lpForm.commitmentAmount}
                        onChange={(e) => setLpForm({ ...lpForm, commitmentAmount: e.target.value })}
                        error={lpForm.commitmentAmount ? lpPlan.errors.commitmentAmount : undefined}
                      />
                      <Input
                        id="lp-ownership"
                        label="Ownership (%)"
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={lpForm.interestPercent}
                        onChange={(e) => setLpForm({ ...lpForm, interestPercent: e.target.value })}
                        helperText="Optional. A collateral assessment needs it."
                        error={lpForm.interestPercent ? lpPlan.errors.interestPercent : undefined}
                      />
                      <Input
                        id="lp-called"
                        label="Called to date"
                        type="number"
                        min={0}
                        value={lpForm.calledAmount}
                        onChange={(e) => setLpForm({ ...lpForm, calledAmount: e.target.value })}
                        error={lpForm.calledAmount ? lpPlan.errors.calledAmount : undefined}
                      />
                      <Input
                        id="lp-distributed"
                        label="Distributed to date"
                        type="number"
                        min={0}
                        value={lpForm.distributedAmount}
                        onChange={(e) => setLpForm({ ...lpForm, distributedAmount: e.target.value })}
                        error={lpForm.distributedAmount ? lpPlan.errors.distributedAmount : undefined}
                      />
                      <Input
                        id="lp-nav"
                        label="Current NAV"
                        type="number"
                        min={0}
                        value={lpForm.currentNav}
                        onChange={(e) => setLpForm({ ...lpForm, currentNav: e.target.value })}
                        error={lpForm.currentNav ? lpPlan.errors.currentNav : undefined}
                      />
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button variant="primary" size="sm" onClick={handleAddLp} disabled={isSaving || !lpPlan.body}>
                        {isSaving ? 'Saving...' : 'Add LP interest'}
                      </Button>
                    </div>
                  </div>
                )}

                {isLoadingLp ? (
                  <div className="p-6 text-center">
                    <p className="text-sm font-sans text-tertiary">Loading LP interests...</p>
                  </div>
                ) : lpError ? (
                  <div className="p-6">
                    <p className="text-sm font-sans text-error">LP interests could not be read: {lpError}</p>
                  </div>
                ) : lpInterests.length === 0 ? (
                  <EmptyState
                    title="No LP interests are recorded for this fund"
                    description="Add an LP interest to record its commitment, called capital and NAV."
                  />
                ) : (
                  <DataGrid<LpInterest>
                    data={lpInterests}
                    columns={lpColumns}
                    enablePagination={false}
                    minTableWidth={800}
                    getRowId={(lp) => lp.id}
                  />
                )}
              </div>
            )}
          </div>

          {/* Capital Activity Section */}
          <div className="bg-surface rounded-sm shadow-sm overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-elevated transition-colors"
              onClick={() => setExpandedSection(expandedSection === 'capital' ? null : 'capital')}
              aria-expanded={expandedSection === 'capital'}
              aria-controls="fund-capital-section"
            >
              <span className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-tertiary" aria-hidden="true" />
                <span className="text-sm font-sans font-semibold text-primary">
                  Capital Activity (
                  {isLoadingCapital ? '...' : capitalError ? NOT_RECORDED : capitalActivities.length})
                </span>
              </span>
              {expandedSection === 'capital' ? (
                <ChevronDown className="w-4 h-4 text-tertiary" aria-hidden="true" />
              ) : (
                <ChevronRight className="w-4 h-4 text-tertiary" aria-hidden="true" />
              )}
            </button>

            {expandedSection === 'capital' && (
              <div id="fund-capital-section" className="border-t border-recessed">
                <div className="px-6 py-3 flex justify-end">
                  <Button variant="secondary" size="sm" onClick={() => setShowCapitalForm(!showCapitalForm)}>
                    <Plus className="w-3 h-3" aria-hidden="true" />
                    Record Activity
                  </Button>
                </div>

                {showCapitalForm && (
                  <div className="px-6 pb-4 border-b border-recessed">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Select
                        id="capital-activity-type"
                        label="Activity type"
                        value={capitalForm.activityType}
                        onChange={(e) =>
                          setCapitalForm({
                            ...capitalForm,
                            activityType: e.target.value as CapitalActivityFormData['activityType'],
                          })
                        }
                      >
                        {optionsFor(CAPITAL_ACTIVITY_LABELS).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Input
                        id="capital-amount"
                        label="Amount"
                        type="number"
                        min={0}
                        value={capitalForm.amount}
                        onChange={(e) => setCapitalForm({ ...capitalForm, amount: e.target.value })}
                        error={
                          capitalForm.amount && !capitalAmountValid ? 'Enter an amount greater than 0.' : undefined
                        }
                      />
                      <Input
                        id="capital-date"
                        label="Date"
                        type="date"
                        value={capitalForm.activityDate}
                        onChange={(e) => setCapitalForm({ ...capitalForm, activityDate: e.target.value })}
                      />
                    </div>
                    <div className="mt-3">
                      <Input
                        id="capital-description"
                        label="Description"
                        value={capitalForm.description}
                        onChange={(e) => setCapitalForm({ ...capitalForm, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddCapitalActivity}
                        disabled={isSaving || !capitalAmountValid || !capitalForm.activityDate}
                      >
                        {isSaving ? 'Saving...' : 'Record'}
                      </Button>
                    </div>
                  </div>
                )}

                {isLoadingCapital ? (
                  <div className="p-6 text-center">
                    <p className="text-sm font-sans text-tertiary">Loading capital activity...</p>
                  </div>
                ) : capitalError ? (
                  <div className="p-6">
                    <p className="text-sm font-sans text-error">
                      Capital activity could not be read: {capitalError}
                    </p>
                  </div>
                ) : capitalActivities.length === 0 ? (
                  <EmptyState
                    title="No capital activity is recorded for this fund"
                    description="Record capital calls, distributions and fees as they occur."
                  />
                ) : (
                  <DataGrid<CapitalActivity>
                    data={capitalActivities}
                    columns={capitalColumns}
                    enablePagination={false}
                    minTableWidth={640}
                    getRowId={(activity) => activity.id}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fund Create/Edit Dialog */}
      <Dialog
        open={showFundModal}
        onOpenChange={(open) => {
          if (!open) setShowFundModal(false);
        }}
        title={editingFund ? 'Edit Fund' : 'Create Fund'}
        size="full"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setShowFundModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveFund}
              disabled={isSaving || hasFormErrors || noChanges}
            >
              {isSaving ? 'Saving...' : editingFund ? 'Save Changes' : 'Create Fund'}
            </Button>
          </>
        }
      >
        <div className="max-h-[65vh] overflow-y-auto space-y-4 pr-1">
          {noChanges && (
            <p className="text-sm font-sans text-tertiary">Edit a field to enable Save Changes.</p>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="fund-name"
              label="Fund name"
              value={fundForm.name}
              onChange={(e) => setField({ name: e.target.value })}
              error={fieldError('name')}
            />
            <Input
              id="fund-manager-name"
              label="Manager name"
              value={fundForm.managerName}
              onChange={(e) => setField({ managerName: e.target.value })}
              error={fieldError('managerName')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {editingFund ? (
              <div>
                <p className="text-xs font-sans font-semibold uppercase tracking-wider text-tertiary mb-1">
                  Manager ID
                </p>
                <p className="text-sm font-mono text-secondary break-all">{editingFund.managerId}</p>
                <p className="mt-1 text-xs font-sans text-tertiary">
                  Set when the fund is created; the update contract does not change it.
                </p>
              </div>
            ) : (
              <Input
                id="fund-manager-id"
                label="Manager ID"
                value={fundForm.managerId}
                onChange={(e) => setField({ managerId: e.target.value })}
                placeholder="Manager identifier (UUID)"
                error={fieldError('managerId')}
              />
            )}
            <Select
              id="fund-strategy"
              label="Strategy"
              value={fundForm.strategy}
              onChange={(e) => setField({ strategy: e.target.value as FundFormData['strategy'] })}
            >
              {optionsFor(FUND_STRATEGY_LABELS).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              id="fund-sector"
              label="Sector focus"
              value={fundForm.sectorFocus}
              onChange={(e) => setField({ sectorFocus: e.target.value as FundFormData['sectorFocus'] })}
              error={fieldError('sectorFocus')}
            >
              <option value="">Not specified</option>
              {sectorOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {editingFund ? (
              <Select
                id="fund-status"
                label="Fund status"
                value={fundForm.fundStatus}
                onChange={(e) => setField({ fundStatus: e.target.value as FundFormData['fundStatus'] })}
              >
                {(Object.keys(FUND_STATUS) as Array<FundFormData['fundStatus']>).map((s) => (
                  <option key={s} value={s}>
                    {FUND_STATUS[s].label}
                  </option>
                ))}
              </Select>
            ) : (
              <div />
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              id="fund-vintage"
              label="Vintage year"
              type="number"
              value={fundForm.vintageYear}
              onChange={(e) => setField({ vintageYear: e.target.value })}
              error={fieldError('vintageYear')}
            />
            <Input
              id="fund-size"
              label="Fund size (committed capital)"
              type="number"
              min={0}
              value={fundForm.fundSize}
              onChange={(e) => setField({ fundSize: e.target.value })}
              error={fieldError('fundSize')}
            />
            <Select
              id="fund-currency"
              label="Currency"
              value={fundForm.currency}
              onChange={(e) => setField({ currency: e.target.value })}
            >
              {[...new Set([fundForm.currency, ...CURRENCY_OPTIONS])].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              id="fund-term"
              label="Fund term (years)"
              type="number"
              min={1}
              max={30}
              value={fundForm.fundTermYears}
              onChange={(e) => setField({ fundTermYears: e.target.value })}
              error={fieldError('fundTermYears')}
            />
            <Input
              id="fund-extension"
              label="Extension (years)"
              type="number"
              min={0}
              value={fundForm.extensionYears}
              onChange={(e) => setField({ extensionYears: e.target.value })}
              error={fieldError('extensionYears')}
            />
            <Input
              id="fund-investment-period"
              label="Investment period (years)"
              type="number"
              min={0}
              value={fundForm.investmentPeriodYears}
              onChange={(e) => setField({ investmentPeriodYears: e.target.value })}
              error={fieldError('investmentPeriodYears')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              id="fund-management-fee"
              label="Management fee (%)"
              type="number"
              step={0.01}
              value={fundForm.managementFeePercent}
              onChange={(e) => setField({ managementFeePercent: e.target.value })}
              error={fieldError('managementFeePercent')}
            />
            <Input
              id="fund-carry"
              label="Carried interest (%)"
              type="number"
              step={0.01}
              value={fundForm.carriedInterestPercent}
              onChange={(e) => setField({ carriedInterestPercent: e.target.value })}
              error={fieldError('carriedInterestPercent')}
            />
            <Input
              id="fund-preferred-return"
              label="Preferred return (%)"
              type="number"
              step={0.01}
              value={fundForm.preferredReturnPercent}
              onChange={(e) => setField({ preferredReturnPercent: e.target.value })}
              error={fieldError('preferredReturnPercent')}
            />
            <Select
              id="fund-waterfall"
              label="Waterfall"
              value={fundForm.waterfallType}
              onChange={(e) => setField({ waterfallType: e.target.value as FundFormData['waterfallType'] })}
              error={fieldError('waterfallType')}
            >
              <option value="">Not specified</option>
              {optionsFor(WATERFALL_LABELS).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="fund-gp-commitment"
              label="GP commitment"
              type="number"
              min={0}
              value={fundForm.gpCommitment}
              onChange={(e) => setField({ gpCommitment: e.target.value })}
              error={fieldError('gpCommitment')}
            />
          </div>
        </div>
      </Dialog>

      {/* Pledge Record/Clear Dialog (PATCH .../lp-interests/{interestId}) */}
      <Dialog
        open={pledgeDialog !== null}
        onOpenChange={(open) => {
          if (!open) setPledgeDialog(null);
        }}
        title={pledgeDialog?.mode === 'clear' ? 'Clear Pledge' : 'Record Pledge'}
        size="lg"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setPledgeDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePledge}
              disabled={isSaving || (pledgeDialog?.mode === 'record' && !pledgePlan.body)}
            >
              {isSaving ? 'Saving...' : pledgeDialog?.mode === 'clear' ? 'Clear pledge' : 'Record pledge'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {pledgeDialog?.mode === 'clear' ? (
            <p className="text-sm font-sans text-secondary">
              The pledge recorded on {pledgeDialog.lp.lpName} is removed together with its loan,
              date and assigned LTV. Record the pledge again to restore them.
            </p>
          ) : (
            <>
              <p className="text-sm font-sans text-secondary">
                Record the pledge on {pledgeDialog?.lp.lpName}. The lending institution is recorded as
                your institution.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  id="pledge-loan-id"
                  label="Loan ID"
                  value={pledgeForm.loanId}
                  onChange={(e) => setPledgeField({ loanId: e.target.value })}
                  placeholder="Loan identifier (UUID)"
                  error={pledgeFieldError('loanId')}
                />
                <Input
                  id="pledge-date"
                  label="Pledge date"
                  type="date"
                  value={pledgeForm.pledgeDate}
                  onChange={(e) => setPledgeField({ pledgeDate: e.target.value })}
                  error={pledgeFieldError('pledgeDate')}
                />
                <Input
                  id="pledge-assigned-ltv"
                  label="Assigned LTV (%)"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={pledgeForm.assignedLtvPercent}
                  onChange={(e) => setPledgeField({ assignedLtvPercent: e.target.value })}
                  helperText="Entered as a percent; recorded as a fraction of the collateral value."
                  error={pledgeFieldError('assignedLtvPercent')}
                />
                <Input
                  id="pledge-ucc-filing"
                  label="UCC filing number"
                  value={pledgeForm.uccFilingNumber}
                  onChange={(e) => setPledgeField({ uccFilingNumber: e.target.value })}
                  helperText="Optional."
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Checkbox
                  id="pledge-gp-consent"
                  label="GP consent obtained"
                  checked={pledgeForm.gpConsentObtained}
                  onChange={(checked) => setPledgeField({ gpConsentObtained: checked })}
                />
                <Input
                  id="pledge-gp-consent-date"
                  label="GP consent date"
                  type="date"
                  value={pledgeForm.gpConsentDate}
                  onChange={(e) => setPledgeField({ gpConsentDate: e.target.value })}
                  helperText="Optional."
                  error={pledgeFieldError('gpConsentDate')}
                />
              </div>
            </>
          )}
          {pledgeError && <Alert type="error">{pledgeError}</Alert>}
        </div>
      </Dialog>
    </div>
  );
}
